import { Eye, FileText, FileType } from 'lucide-react';
import resumes from '@/data/resumes.json';
import { asset } from '@/lib/paths';
import { Section } from '@/components/layout/Section';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Reveal } from '@/components/ui/Reveal';

const kb = (bytes: number) => `${Math.round(bytes / 1024)} kB`;

/**
 * Download cards driven by the generated manifest, so adding a resume variant
 * needs no edit here (PLAN.md §8.8).
 */
export function Resumes() {
  return (
    <Section
      id="resume"
      title="Resume"
      lead="Three versions of the same facts, ordered for different readers."
    >
      <div className="grid gap-4 sm:grid-cols-3">
        {resumes.map((variant, i) => (
          <Reveal key={variant.id} index={i}>
            <Card className="flex h-full flex-col">
              <h3 className="font-semibold text-foreground">{variant.label}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{variant.description}</p>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {variant.targetRoles.map((role) => (
                  <Badge key={role} variant="outline">
                    {role}
                  </Badge>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap gap-2 pt-1">
                {/*
                  A plain link to the PDF, opened in a new tab, rather than an
                  in-page modal. An <iframe> preview depends on the browser
                  shipping a PDF viewer and being willing to render it framed —
                  Firefox and mobile Safari often refuse, and the reader gets a
                  blank panel or "refused to connect". A new tab hands the file
                  to the browser's own viewer, which always works.

                  No `download` attribute here: that would force a save instead
                  of displaying it, which is what the PDF button beside this is
                  for.
                */}
                <a
                  href={asset(`resumes/${variant.files.pdf}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open the ${variant.label} resume in a new tab`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                  Preview
                </a>
                <a
                  href={asset(`resumes/${variant.files.pdf}`)}
                  download
                  aria-label={`Download the ${variant.label} resume as PDF, ${kb(variant.bytes.pdf)}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                  <span aria-hidden>PDF — to send</span>
                  <span aria-hidden className="font-normal opacity-75">
                    {kb(variant.bytes.pdf)}
                  </span>
                </a>
                <a
                  href={asset(`resumes/${variant.files.docx}`)}
                  download
                  aria-label={`Download the ${variant.label} resume as Word document, ${kb(variant.bytes.docx)}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  <FileType className="h-3.5 w-3.5" aria-hidden />
                  <span aria-hidden>DOCX — to upload</span>
                  <span aria-hidden className="font-normal text-muted-foreground">
                    {kb(variant.bytes.docx)}
                  </span>
                </a>
              </div>
            </Card>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
